import React, { useState, ReactNode } from 'react';
import { useAuth0, useUserAuth, handleLogout } from '@lucid/auth';
import Sidebar, { SidebarLabels, NavigationItem } from '../Sidebar/Sidebar';
import styles from './Layout.module.scss';

interface LayoutProps {
  children?: ReactNode;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  showRoleDropdown?: boolean;
  currentPath?: string;
  /** Current locale/region (e.g., 'de-de', 'en-us'). Controls the sidebar region dropdown. */
  locale?: string;
  onLocaleChange?: (locale: string) => void;
  /** Currently selected label language (null = native locale language) */
  selectedLanguage?: string | null;
  /** Callback when label language changes */
  onLanguageChange?: (languageCode: string | null) => void;
  /** Controlled collapsed state for the sidebar */
  isSidebarCollapsed?: boolean;
  /** Callback when sidebar collapsed state changes */
  onSidebarCollapseChange?: (collapsed: boolean) => void;
  /** i18n labels for sidebar localization */
  sidebarLabels?: SidebarLabels;
  /** Navigation items to display in sidebar. If not provided, uses default sidebar.json */
  navigationItems?: NavigationItem[];
  /** Callback for client-side navigation. If not provided, sidebar falls back to window.location.href */
  onNavigate?: (path: string) => void;
  /** Route prefixes owned by the current app. Paths not matching any prefix trigger a full page load. */
  ownRoutePrefixes?: string[];
  /** Optional link rendered at the top of the Settings drawer (e.g. Knowledge Center) */
  knowledgeCenterLink?: { href: string; label: string };
  /** Auth0 roles for the current user. Passed through to the Sidebar for role-scoped navigation. */
  userRoles?: string[];
  /** Auth0 roles namespace used in the ID token (e.g. 'https://yourdomain.com/roles'). */
  rolesNamespace?: string;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  theme = 'dark',
  onToggleTheme,
  showRoleDropdown = false,
  currentPath,
  locale,
  onLocaleChange,
  selectedLanguage = null,
  onLanguageChange,
  isSidebarCollapsed: isSidebarCollapsedProp,
  onSidebarCollapseChange,
  sidebarLabels,
  navigationItems,
  onNavigate,
  ownRoutePrefixes,
  knowledgeCenterLink,
  userRoles,
  rolesNamespace,
}) => {
  const { logout } = useAuth0();
  const { user, roles } = useUserAuth(rolesNamespace);
  const [internalCollapsed, setInternalCollapsed] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isSidebarCollapsed = isSidebarCollapsedProp ?? internalCollapsed;

  const handleCollapseChange = (collapsed: boolean) => {
    if (onSidebarCollapseChange) {
      onSidebarCollapseChange(collapsed);
    } else {
      setInternalCollapsed(collapsed);
    }
  };

  const onSignOut = () => {
    handleLogout(logout);
  };

  return (
    <div className={styles.layout} data-theme={theme}>
      <Sidebar
        theme={theme}
        userName={user?.name as string | undefined}
        userEmail={user?.email as string | undefined}
        onToggleTheme={onToggleTheme}
        isCollapsed={isSidebarCollapsed}
        onCollapseChange={handleCollapseChange}
        onSignOut={onSignOut}
        showRoleDropdown={showRoleDropdown}
        currentPath={currentPath}
        locale={locale}
        onLocaleChange={onLocaleChange}
        selectedLanguage={selectedLanguage}
        onLanguageChange={onLanguageChange}
        labels={sidebarLabels}
        navigationItems={navigationItems}
        onNavigate={onNavigate}
        ownRoutePrefixes={ownRoutePrefixes}
        knowledgeCenterLink={knowledgeCenterLink}
        userRoles={userRoles ?? roles}
      />
      <main
        className={`${styles.main} ${isSidebarCollapsed ? styles.mainWithCollapsedSidebar : ''}`}
        data-theme={theme}
      >
        <div className={styles.content}>{children}</div>
      </main>
    </div>
  );
};

Layout.displayName = 'Layout';

export default Layout;
